import React, { useState, useContext, useRef, useEffect } from 'react'
import { getPersonalityData } from 'services/break.js'
import ServerError from 'steps/shared/ServerError'
import { userContext } from 'global/context/sub-contexts/userContext'
import Slider from 'react-slick'

import DropdownSection from './parts/DropdownSection'
import ColumnsSection from './parts/ColumnsSection'
import StartSlide from 'steps/StartSlide'
import SkipButton from 'steps/SkipButton'
import ArrowButton from 'images/inline/arrow-right.svg'

import './ReferralStep.scss'
import IntersectionVisible from 'components/IntersectionVisible'
import LegacyImage from 'components/LegacyImage'

import {
  start as serviceStart,
  complete as serviceComplete,
} from 'services/referral'
import analytics from 'global/AnalyticsData'

const ReferralStep = ({
  brk,
  breakId,
  step,
  onGoToNext,
  onStepStart,
  onStepComplete,
  onStepSkip,
  isEmbed,
  unlockNextStep,
  scrollComplete,
  isComplete,
  onAuthRequired,
  onUnhandledError,
  HtmlFooter,
}) => {
  const [pqResultId, setPqResultId] = useState()
  const [stepState, setStepState] = useState('NOT_STARTED') // NOT_STARTED, IN_PROGRESS, COMPLETE
  const [interacted, setInteracted] = useState(false)
  const [serverError, setIsServerError] = useState(false)
  const [stepSkipped, setStepSkipped] = useState(false)

  const sliderRef = useRef(null)

  //state funcs converted to ref funcs
  const sectionEl = useRef(null)
  const nextArrowEl = useRef(null)

  const setArrowLocked = () => {
    if (!nextArrowEl.current) return
    nextArrowEl.current.disabled = true
  }

  const setArrowUnlocked = () => {
    if (!nextArrowEl.current) return
    nextArrowEl.current.disabled = false
  }

  const setWaitingForServer = state => {
    if (state === true) {
      sectionEl.current.classList.remove('waiting-false')
      sectionEl.current.classList.add('waiting-true')
    } else {
      sectionEl.current.classList.remove('waiting-true')
      sectionEl.current.classList.add('waiting-false')
    }
  }

  const user_context = useContext(userContext)

  let ReferralObject = step.referralData
  ReferralObject.links = step.links
  const layout = ReferralObject.layout
  const dropdownLinks = ReferralObject.links.filter(
    link => link.buttonType === 'MENU',
  )
  const buttonLinks = ReferralObject.links.filter(
    link => link.buttonType === 'BUTTON',
  )

  const sliderSettings = {
    dots: false,
    arrows: false,
    swipe: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    adaptiveHeight: true,
    slidesToScroll: 1,
  }

  // console.log('referral: ', step)

  const getPersonalityQuizResultId = async () => {
    try {
      const response = await getPersonalityData(breakId)
      //   console.log(response)
      setPqResultId(response.detail.resultId)
    } catch (e) {
      console.log(e)
      if (e.message === '401') {
        onAuthRequired && onAuthRequired()
      } else {
        onUnhandledError && onUnhandledError()
      }
    }
  }

  const handleNextClick = async () => {
    setWaitingForServer(true)
    console.log('Next Click')

    if (stepState === 'NOT_STARTED') {
      try {
        const res = await serviceStart(breakId, step.id)

        if (onStepStart) {
          const { attempt, firstBreakEngagement } = res
          onStepStart(step, attempt, firstBreakEngagement)
        }

        setWaitingForServer(false)
        console.log('Referral Start')
        setStepState('STARTED')
        await getPersonalityQuizResultId()
        sliderRef.current.slickNext()
      } catch (e) {
        if (e.message === '401') {
          onAuthRequired && onAuthRequired()
        } else {
          onUnhandledError && onUnhandledError()
        }
      }
    }

    if (stepState === 'STARTED') {
      handleCompleteClick()
    }
  }

  const closeDropdowns = e => {
    const openDropdowns = [
      ...document.getElementsByClassName('dropdown-cover open'),
    ]
    if (!e.target.matches('.dropdown-card *')) {
      openDropdowns.length &&
        openDropdowns.forEach(dropdown => dropdown.classList.remove('open'))
    }
  }

  const setInteractedAndUnlock = () => {
    setInteracted(true)
    unlockNextStep && unlockNextStep(step.id)
  }

  const handleLinkClick = link => {
    if (typeof window !== `undefined`) {
      window.dataLayer.push({
        event: 'step_interaction_referral_click',
      })

      analytics.stepEvent('step_interaction_referral_click', step, {
        result_id: link.id,
        result: link.url,
      })
    }

    setInteracted(true)
    unlockNextStep && unlockNextStep(step.id)
  }

  const handleCompleteClick = async () => {
    //When unhandled issues occur on start/complete, need to handle these without breaking the page in a try block.
    try {
      const response = await serviceComplete(breakId, step.id, false)
      const { totalPoints } = response.detail
      user_context.setPoints(totalPoints)
    } catch (e) {
      console.warn(`issue completing referral step: ${e}`)
    }
    setWaitingForServer(false)
    console.log('Referral Complete')
    !stepSkipped && onStepComplete && onStepComplete(step.id)
    onGoToNext && onGoToNext(step.id)
    setStepState('COMPLETE')
    setArrowLocked()
  }

  const handleStepSkip = async () => {
    try {
      const response = await serviceComplete(breakId, step.id, true)
    } catch (e) {
      console.warn(`issue completing referral step: ${e}`)
    }
    setWaitingForServer(false)
    onStepSkip && onStepSkip(step.id)
    setStepSkipped(true)
    setStepState('COMPLETE')
  }

  // our intersection observer told us we are on screen.
  const handleIsOnScreen = isIntersecting => {
    if (!step.hasStartScreen && isIntersecting && stepState === 'NOT_STARTED') {
      handleNextClick()
    }
  }

  //listen for scrollComplete and fire off handleCompleteClick when toggled
  useEffect(() => {
    if (scrollComplete && !isComplete) {
      handleCompleteClick()
    }
  }, [scrollComplete, isComplete])

  let style = {}
  const bgImg =
    step && step.backgroundImage
      ? step.backgroundImage.id
      : step.backgroundImageId

  if (step && step.backgroundColor) {
    style.backgroundColor = step.backgroundColor
  }

  if (bgImg) {
    style.backgroundImage = `url(${process.env.SITE_ASSET_URL}/${bgImg})`
  }

  return (
    <div className="sponsored-flow-panel" style={style}>
      <IntersectionVisible
        active={true}
        options={{
          threshold: 0.4,
        }}
        onIntersect={handleIsOnScreen}
      >
        <section
          ref={sectionEl}
          className={`referral-container continue-ready waiting-false`}
        >
          <>
            <Slider
              ref={slider => (sliderRef.current = slider)}
              {...sliderSettings}
              className="quiz__slider"
            >
              {step.hasStartScreen && (
                <StartSlide
                  headline={step.startHeader}
                  para={step.startBody}
                  footer={step.startFooter}
                  backgroundImageId={step.startBackgroundImageId}
                  backgroundColor={step.startBackgroundColor}
                  onStartClicked={handleNextClick}
                  stepType={step.type}
                  // bgImageExists={style.backgroundImage && true}
                />
              )}
              <div>
                <section
                  className="referral-step"
                  onClick={closeDropdowns.bind(this)}
                >
                  {bgImg && <span className="shade"></span>}
                  {ReferralObject.headline && (
                    <h1
                      style={
                        {
                          // background: brk.missionColor1,
                          //color: brk.missionColor6,
                        }
                      }
                      dangerouslySetInnerHTML={{
                        __html: ReferralObject.headline,
                      }}
                    />
                  )}

                  {ReferralObject.introCopy && (
                    <p
                      style={{ color: brk.missionColor6 }}
                      dangerouslySetInnerHTML={{
                        __html: ReferralObject.introCopy,
                      }}
                    ></p>
                  )}

                  {layout === 'ONE_COLUMN' && (
                    <div className="referral-body">
                      {dropdownLinks.length > 0 && (
                        <DropdownSection
                          dropdownLinks={dropdownLinks}
                          layout={layout}
                          pqResultId={pqResultId}
                          setInteractedAndUnlock={setInteractedAndUnlock}
                          step={step}
                        />
                      )}
                      <div className="referral-image-single">
                        {/* TODO - smelly: either use better validation or change the backend */}
                        {ReferralObject.links[0].url &&
                        ReferralObject.links[0].url !== 'https://' ? (
                          <a
                            href={ReferralObject.links[0].url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <LegacyImage image={ReferralObject.headerImage} />
                          </a>
                        ) : (
                          <LegacyImage image={ReferralObject.headerImage} />
                        )}
                      </div>
                      {buttonLinks.length > 0 && (
                        <ColumnsSection
                          buttonLinks={buttonLinks}
                          layout={layout}
                          pqResultId={pqResultId}
                          onLinkClick={handleLinkClick}
                        />
                      )}
                    </div>
                  )}

                  {layout !== 'ONE_COLUMN' && (
                    <div className="referral-body">
                      {dropdownLinks.length > 0 && (
                        <DropdownSection
                          dropdownLinks={dropdownLinks}
                          layout={layout}
                          pqResultId={pqResultId}
                          setInteractedAndUnlock={setInteractedAndUnlock}
                          step={step}
                        />
                      )}
                      {buttonLinks.length > 0 && (
                        <ColumnsSection
                          buttonLinks={buttonLinks}
                          layout={layout}
                          pqResultId={pqResultId}
                          onLinkClick={handleLinkClick}
                        />
                      )}
                    </div>
                  )}
                </section>
                {serverError && <ServerError />}
              </div>
            </Slider>
            {stepState !== 'NOT_STARTED' && (
              <button
                ref={nextArrowEl}
                className={`quiz-button2 done`}
                onClick={handleNextClick}
                disabled={!interacted || stepState === 'COMPLETE'}
                aria-label="Next"
              >
                <ArrowButton />
              </button>
            )}
            {HtmlFooter && <HtmlFooter />}
            {stepState !== 'NOT_STARTED' &&
              !interacted &&
              step.showSkipButton && <SkipButton onClick={handleStepSkip} />}
          </>
        </section>
      </IntersectionVisible>
    </div>
  )
}

export default ReferralStep
